import { createAppSlice } from "../../app/createAppSlice"
import { createEntityAdapter } from '@reduxjs/toolkit'
import { selectCurrentUserApiToken } from "../currentUser/currentUserSlice"
import getUserImportanceRatingDictionary from "../../functions/getUserImportanceRatingDictionaryAPI"
import { selectAllOutcomeVariables } from "../outcomeVariables/outcomeVariablesSlice"

const fetchUserImportanceRatingsAPI = async (apiToken, outcomeVariables) => {
    let userImportanceEntities = [];
    let ratingDictionary;
                console.log("about to call getUserImportanceRatingDictionary")
    let response = await getUserImportanceRatingDictionary(apiToken);

    if (response?.success && response.userImportanceRatingDictionary) {
        ratingDictionary = response.userImportanceRatingDictionary;
        //response includes { "success": true,
        //  "userImportanceRatingDictionary": userImportanceRatingDictionary }
        // where userImportanceRatingDictionary uses foi as key (foi is for EvidenceVariable) and object with
        //    {
        //        "desirable": boolean/null, 
        //        "relativeimportance": number/NaN
        //    }
        if (ratingDictionary && Object.keys(ratingDictionary).length > 0) {
            for (let targetFoi in ratingDictionary) {
                let entity = {
                    id: targetFoi,
                    exists: true,
                    desirability: ratingDictionary[targetFoi].desirable,
                    percentageValue: ratingDictionary[targetFoi].relativeimportance,
                    itemChanged: false
                };
                userImportanceEntities.push(entity);
            }
            for (let item of outcomeVariables) {
                let targetFoi = item.id;
                if (!ratingDictionary[targetFoi]) {
                    let entity = {
                        id: targetFoi,
                        exists: false,
                        desirability: null,
                        percentageValue: NaN,
                        itemChanged: false
                    };
                    userImportanceEntities.push(entity);
                }
            }
        }
    } else {
        console.log("FAILURE getting userImportanceRatingDictionary");
    }

    return userImportanceEntities;
}

//entity: {  id: targetFoi,  for EvidenceVariable
//  exists: boolean,
//  desirability: ratingDictionary[targetFoi].desirable,
//  percentageValue: ratingDictionary[targetFoi].relativeimportance,
//  itemChanged: false
// 
//key uses: customized Summary of Net Effect, Rate Relative Importance

const userImportanceRatingsAdapter = createEntityAdapter({
    sortComparer: (b, a) => {
        if (b?.targetFoi && a?.targetFoi && b.targetFoi !== a.targetFoi) {
            return b.targetFoi.localeCompare(a.targetFoi)
        } else {
            return 0
        }
    }
})

const initialState = userImportanceRatingsAdapter.getInitialState({
    status: 'idle',
    error: null
})

const userImportanceRatingsSlice = createAppSlice({
    name: "userImportanceRatings",
    initialState,
    reducers: create => ({
        userImportanceRatingAdded: create.preparedReducer(
            (importanceBody) => {
                return {
                    payload: {
                        id: importanceBody.id,
                        exists: importanceBody.exists, 
                        desirability: importanceBody.desirability,
                        percentageValue: importanceBody.percentageValue,
                        itemChanged: true
                    }
                };
            },
            userImportanceRatingsAdapter.addOne
        ),
        userImportanceRatingUpdated: create.reducer((state, action) => {
            const { id, exists, desirability, percentageValue, itemChanged } = action.payload;
            userImportanceRatingsAdapter.updateOne(state,
                {
                    id, changes: {
                        exists, desirability, percentageValue, itemChanged
                    }
                })
        }),
        fetchUserImportanceRatings: create.asyncThunk(
            async (_, { getState }) => {
                const state = getState();
                const apiToken = selectCurrentUserApiToken(state);
                const outcomeVariables = selectAllOutcomeVariables(state);
                const resourceEntities = await fetchUserImportanceRatingsAPI(apiToken, outcomeVariables);
                return resourceEntities
            },
            {
                /*
                 options: {
                     condition(arg, thunkApi) {
                         const { userImportanceRatings } = thunkApi.getState()
                         if (userImportanceRatings.status !== 'idle') {
                             return false
                         }
                     }
                 },
                 */
                pending: state => {
                    state.status = "loading"
                },
                fulfilled: (state, action) => {
                    state.status = "succeeded"
                    userImportanceRatingsAdapter.setAll(state, action.payload)
                },
                rejected: (state, action) => {
                    state.status = "failed"
                    state.error = action.error.message ?? "Unknown Error"
                },
            },
        ),
    }),
    selectors: {
        selectUserImportanceRatingsStatus: userImportanceRatings => userImportanceRatings.status,
        selectUserImportanceRatingsError: userImportanceRatings => userImportanceRatings.error
    },
})

export const { userImportanceRatingAdded, userImportanceRatingUpdated, fetchUserImportanceRatings } = userImportanceRatingsSlice.actions

export const { selectUserImportanceRatingsStatus, selectUserImportanceRatingsError } = userImportanceRatingsSlice.selectors

export const {
    selectAll: selectAllUserImportanceRatings,
    selectById: selectUserImportanceRatingByTargetId,
} = userImportanceRatingsAdapter.getSelectors(state => state.userImportanceRatings)

export default userImportanceRatingsSlice